/**
 * PhotoPrintSkuPicker - SKU picker for photo print products
 *
 * Extends the base SkuPicker with photo upload functionality:
 * - Photo upload button row in configurator
 * - Photo dimension tracking for size recommendations
 * - Session storage persistence for uploaded photos
 *
 * Used for products where customers upload their own photos for printing.
 */

class PhotoPrintSkuPicker extends SkuPicker {
  constructor(containerId, options = {}) {
    // Always enable photo upload for this class
    super(containerId, { ...options, showPhotoUpload: false }); // Base class doesn't need this flag

    // Photo-specific state
    this.uploadedPhotoDimensions = null;
  }

  /**
   * Override _initialize to add photo upload listeners after base initialization
   */
  _initialize() {
    super._initialize();
    this._setupPhotoUploadListeners();
  }

  /**
   * Set up photo upload event listeners (called once during initialization)
   */
  _setupPhotoUploadListeners() {
    // Listen for photo upload changes to update the button UI
    document.addEventListener('photo-upload:changed', (e) => {
      // Store photo dimensions for size filtering
      if (e.detail.width && e.detail.height) {
        this.uploadedPhotoDimensions = {
          width: e.detail.width,
          height: e.detail.height
        };
      }
      this._updatePhotoUploadButton(e.detail.fileName, e.detail.fileType, e.detail.width, e.detail.height);
    });

    document.addEventListener('photo-upload:removed', () => {
      this.uploadedPhotoDimensions = null;
      this._updatePhotoUploadButton(null);
    });
  }

  /**
   * Override _setupEventDelegation - photo upload component handles its own events
   */
  _setupEventDelegation() {
    super._setupEventDelegation();
  }

  /**
   * Override _performModalUIUpdate to add photo resolution-based size filtering
   */
  _performModalUIUpdate(modalEl, optionType) {
    // Call parent implementation first
    super._performModalUIUpdate(modalEl, optionType);

    // Add resolution-based filtering for size options
    if (optionType === 'size' && this.uploadedPhotoDimensions) {
      const grid = modalEl.querySelector('.option-grid-container');
      let hasUnavailableSizes = false;

      grid.querySelectorAll('.selectable').forEach(button => {
        const handle = button.dataset.optionHandle;
        const sizeOption = this.data.options.size.find(s => s.handle === handle);

        if (sizeOption && !this._photoMeetsMinResolutionForSize(sizeOption)) {
          button.classList.add('disabled', 'resolution-limited');
          hasUnavailableSizes = true;
        }
      });

      // Show/hide resolution message
      let messageEl = modalEl.querySelector('.size-resolution-message');
      if (hasUnavailableSizes) {
        if (!messageEl) {
          messageEl = document.createElement('div');
          messageEl.className = 'size-resolution-message';
          messageEl.innerHTML = `<span class="resolution-icon">ℹ</span> ${this._t('size_resolution_message', 'For larger sizes, upload a higher resolution photo to ensure the perfect print')}`;
          const infoContainer = modalEl.querySelector('.size-info-message');
          if (infoContainer) {
            infoContainer.appendChild(messageEl);
          }
        }
        messageEl.style.display = '';
      } else if (messageEl) {
        messageEl.style.display = 'none';
      }
    }
  }

  /**
   * Check if uploaded photo meets minimum resolution for a size option.
   *
   * This accounts for aspect ratio cropping - a photo may have enough total
   * megapixels but insufficient resolution after cropping to the print's
   * aspect ratio.
   *
   * Example: A 6000×2000 photo (12MP) for a 12×12 square print:
   * - Total MP check would pass (12MP > 3.24MP required)
   * - But when cropped to square, only 2000×2000 = 4MP is usable
   *
   * We check both orientations (original and rotated) and allow if either works.
   */
  _photoMeetsMinResolutionForSize(sizeOption) {
    if (!this.uploadedPhotoDimensions) return true;

    const { width: photoWidth, height: photoHeight } = this.uploadedPhotoDimensions;
    const minDPI = 150;

    const printWidthInches = parseFloat(sizeOption.width_inches) || 0;
    const printHeightInches = parseFloat(sizeOption.height_inches) || 0;

    if (!printWidthInches || !printHeightInches) return true;

    // Required pixels for this print size at minimum DPI
    const requiredWidth = printWidthInches * minDPI;
    const requiredHeight = printHeightInches * minDPI;

    // Check if photo meets requirements in original orientation
    if (this._checkCroppedResolution(photoWidth, photoHeight, requiredWidth, requiredHeight)) {
      return true;
    }

    // Check if photo meets requirements when rotated 90°
    // (user might rotate their photo to better fit the print orientation)
    if (this._checkCroppedResolution(photoHeight, photoWidth, requiredWidth, requiredHeight)) {
      return true;
    }

    return false;
  }

  /**
   * Check if photo dimensions meet requirements after cropping to target aspect ratio.
   *
   * @param {number} photoWidth - Photo width in pixels
   * @param {number} photoHeight - Photo height in pixels
   * @param {number} requiredWidth - Required width in pixels (print width × DPI)
   * @param {number} requiredHeight - Required height in pixels (print height × DPI)
   * @returns {boolean} True if cropped photo meets minimum resolution
   */
  _checkCroppedResolution(photoWidth, photoHeight, requiredWidth, requiredHeight) {
    // Target aspect ratio from print dimensions
    const targetAspect = requiredHeight / requiredWidth;
    const photoAspect = photoHeight / photoWidth;

    let croppedWidth, croppedHeight;

    if (photoAspect > targetAspect) {
      // Photo is taller than target - crop top/bottom, width stays full
      croppedWidth = photoWidth;
      croppedHeight = photoWidth * targetAspect;
    } else {
      // Photo is wider than target - crop sides, height stays full
      croppedHeight = photoHeight;
      croppedWidth = photoHeight / targetAspect;
    }

    // Check if cropped dimensions meet minimum requirements
    return croppedWidth >= requiredWidth && croppedHeight >= requiredHeight;
  }

  /**
   * Override _renderOptionRows - photo upload row is now server-rendered outside the container
   */
  _renderOptionRows() {
    // Use document fragment for better performance - batch all DOM changes
    const fragment = document.createDocumentFragment();

    try {
      // Photo upload row is now server-rendered outside #sku-picker-container
      // so we don't create it here anymore

      fragment.appendChild(this._createRow('size'));
      fragment.appendChild(this._createRow('framing'));

      // Only show border row if there are 2 or more border options available for current size
      const currentSize = this.state.confirmed.size_option;
      const availableBorderOptions = this.components.border[currentSize];
      const availableBorderCount = availableBorderOptions ? Object.keys(availableBorderOptions).length : 0;
      if (availableBorderCount > 1) {
        fragment.appendChild(this._createRow('border'));
      }

      // Single batch DOM update
      requestAnimationFrame(() => {
        this.container.replaceChildren(fragment);

        // Toggle disabled class on container
        this.container.classList.toggle(
          'sku-picker--unavailable',
          !this.currentVariantAvailable || !this.currentSelectionDetails.available
        );

        this._attachEventListeners();

        // Restore photo upload button state from sessionStorage if needed
        this._restorePhotoUploadButtonState();
      });
    } catch (error) {
      this.container.innerHTML = '<div style="padding: 20px; color: #d72c2c;">Error rendering configurator: ' + error.message + '</div>';
    }
  }

  /**
   * Create photo upload button row
   */
  _createPhotoUploadRow() {
    // Get translations from container data attributes (set by Liquid)
    const photoLabel = this.container.dataset.photoLabel || 'Photo';
    const uploadLabel = this.container.dataset.uploadLabel || 'Upload your photo';
    const uploadSublabel = this.container.dataset.uploadSublabel || 'Add your image to see it framed';
    const uploadedLabel = this.container.dataset.uploadedLabel || 'Uploaded photo';
    const changeText = this.container.dataset.changeText || 'Click to change';

    const row = document.createElement('div');
    row.className = 'option-row option-row--photo-upload';

    row.innerHTML = `
      <div class="option-left"><span>${photoLabel}</span></div>
      <button type="button" class="option-main photo-upload-trigger add-photo-button" data-photo-upload-trigger>
        <div>
          <span class="photo-upload-trigger__label" data-upload-label data-default-text="${uploadLabel}" data-uploaded-text="${uploadedLabel}">${uploadLabel}</span>
          <span class="photo-upload-trigger__sublabel" data-upload-sublabel data-default-text="${uploadSublabel}" data-change-text="${changeText}">${uploadSublabel}</span>
        </div>
        <span class="plus-icon">+</span>
      </button>
    `;

    return row;
  }

  /**
   * Update photo upload button state
   */
  _updatePhotoUploadButton(fileName, fileType, width, height) {
    const trigger = this.container.querySelector('[data-photo-upload-trigger]');
    if (!trigger) return;

    const label = trigger.querySelector('[data-upload-label]');
    const sublabel = trigger.querySelector('[data-upload-sublabel]');
    const icon = trigger.querySelector('.plus-icon');

    if (fileName) {
      // Photo uploaded - show "Uploaded photo" and file details
      if (label) {
        label.textContent = label.getAttribute('data-uploaded-text') || 'Uploaded photo';
      }
      if (sublabel) {
        // Format file type (e.g., "image/jpeg" -> "JPEG")
        const fileTypeDisplay = fileType ? fileType.split('/')[1].toUpperCase() : '';
        // Format dimensions in megapixels
        const dimensionsDisplay = (width && height) ? `${width}×${height}px · ${this._formatMegapixels(width * height)}` : '';
        // Combine with "Click to change"
        const parts = [fileTypeDisplay, dimensionsDisplay].filter(Boolean);
        const detailsText = parts.length > 0 ? parts.join(', ') + ' · ' : '';
        sublabel.textContent = detailsText + (sublabel.getAttribute('data-change-text') || 'Click to change');
      }
      if (icon) {
        icon.textContent = '✓';
      }
      trigger.classList.add('photo-uploaded');
    } else {
      // No photo - show default text
      if (label) label.textContent = label.getAttribute('data-default-text') || 'Upload your photo';
      if (sublabel) sublabel.textContent = sublabel.getAttribute('data-default-text') || 'Add your image to see it framed';
      if (icon) icon.textContent = '+';
      trigger.classList.remove('photo-uploaded');
    }
  }

  /**
   * Restore photo upload button state from sessionStorage
   * Called after SKU picker re-renders (e.g., variant change)
   */
  _restorePhotoUploadButtonState() {
    try {
      const stored = sessionStorage.getItem('uploaded_photo');
      if (!stored) return;

      const photoData = JSON.parse(stored);

      // Check if data is recent (within last hour)
      const oneHour = 60 * 60 * 1000;
      if (Date.now() - photoData.timestamp > oneHour) {
        sessionStorage.removeItem('uploaded_photo');
        return;
      }

      // Store photo dimensions for size filtering
      if (photoData.width && photoData.height) {
        this.uploadedPhotoDimensions = {
          width: photoData.width,
          height: photoData.height
        };
      }
      // Update button to show uploaded state
      this._updatePhotoUploadButton(photoData.fileName, photoData.fileType, photoData.width, photoData.height);
    } catch (e) {
      // Silently fail - sessionStorage may be unavailable
    }
  }

  /**
   * Format pixels to megapixels (e.g., 3600000 → "3.6MP")
   */
  _formatMegapixels(pixels) {
    const megapixels = pixels / 1000000;
    if (megapixels >= 10) {
      return `${Math.round(megapixels)}MP`;
    } else {
      return `${megapixels.toFixed(1)}MP`;
    }
  }

  /**
   * Check if uploaded photo meets recommended resolution for a size option
   * @param {Object} sizeOption - Size option with recommended_photo_upload_mp
   * @returns {boolean} - True if photo meets recommendation or no photo uploaded
   */
  _photoMeetsRecommendedResolution(sizeOption) {
    // If no photo uploaded, don't show any recommendation
    if (!this.uploadedPhotoDimensions) {
      return true;
    }

    // If no recommendation set, no warning needed
    if (!sizeOption.recommended_photo_upload_mp) {
      return true;
    }

    const { width, height } = this.uploadedPhotoDimensions;
    const photoMegapixels = (width * height) / 1000000;
    const recommendedMp = parseFloat(sizeOption.recommended_photo_upload_mp);

    return photoMegapixels >= recommendedMp;
  }

  /**
   * DEPRECATED - kept for backward compatibility
   * Check if uploaded photo meets minimum resolution requirements for a size option
   */
  _photoMeetsMinimumResolution(sizeOption) {
    return this._photoMeetsRecommendedResolution(sizeOption);
  }
}

// Export to global scope
window.PhotoPrintSkuPicker = PhotoPrintSkuPicker;

// Initialize SKU picker now that PhotoPrintSkuPicker is available
if (typeof initSkuPicker === 'function') {
  initSkuPicker();
}
